import { v, type Infer } from 'convex/values';
import { conflict, invalid } from './errors';

/** Most ops one `documents.transact` call may carry. */
export const MAX_TRANSACT_OPS = 50;

const META_KEYS = new Set(['createdBy', 'updatedBy', 'createdAt', 'updatedAt']);

/** One write of `documents.transact`; `put`/`patch`/`remove` each run exactly one of these. */
export const documentOp = v.union(
  v.object({ kind: v.literal('put'), collection: v.string(), data: v.any() }),
  v.object({ kind: v.literal('patch'), id: v.id('documents'), data: v.any(), ifUpdatedAt: v.optional(v.number()) }),
  v.object({ kind: v.literal('remove'), id: v.id('documents'), ifUpdatedAt: v.optional(v.number()) }),
);
export type DocumentOp = Infer<typeof documentOp>;

/** Data must be a plain object; keys may not start with "_" or collide with meta fields. */
export function checkData(data: unknown): Record<string, unknown> {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    invalid('data must be a JSON object');
  }
  for (const key of Object.keys(data)) {
    if (key.startsWith('_')) invalid(`data key "${key}" may not start with "_"`);
    if (META_KEYS.has(key)) invalid(`data key "${key}" is reserved`);
  }
  return data as Record<string, unknown>;
}

/** 1–MAX_TRANSACT_OPS ops; throws INVALID otherwise. */
export function checkOpCount(ops: readonly unknown[]): void {
  if (ops.length === 0) invalid('ops must contain at least one operation');
  if (ops.length > MAX_TRANSACT_OPS) invalid(`ops may contain at most ${MAX_TRANSACT_OPS} operations (got ${ops.length})`);
}

/** Optimistic guard: CONFLICT unless the stored `updatedAt` still equals the one the caller saw. */
export function checkGuard(doc: { updatedAt: number }, ifUpdatedAt: number | undefined): void {
  if (ifUpdatedAt !== undefined && doc.updatedAt !== ifUpdatedAt) {
    conflict('This document was changed by someone else since you read it');
  }
}

/**
 * `updatedAt` for a write: strictly greater than the previous one, so a guard never passes
 * against a second write in the same millisecond (Date.now() is fixed for a whole mutation).
 */
export function nextUpdatedAt(previous: number, now: number): number {
  return Math.max(now, previous + 1);
}
